import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  InputNumber, 
  Space, 
  Tabs, 
  Card,
  Alert,
  Button,
  Spin,
  App,
  Checkbox
} from 'antd';
import { Node } from 'reactflow';
import Editor from '@monaco-editor/react';
import { BrainCircuit, Wrench, GitBranch, Settings } from 'lucide-react';
import { apiUtils } from '../../lib/api';

const { Option } = Select;
const { TextArea } = Input;

interface NodeConfigurationModalProps {
  visible: boolean;
  node: Node | null;
  onClose: () => void;
  onSave: (nodeId: string, data: any) => void;
  projectId?: string;
}

const NodeConfigurationModal: React.FC<NodeConfigurationModalProps> = ({
  visible,
  node,
  onClose,
  onSave,
  projectId,
}) => {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const [jsCode, setJsCode] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [hasCustomCode, setHasCustomCode] = useState(false);
  interface DataSourceType {
    id: string;
    name: string;
    description?: string;
    connectorType: string;
    config: Record<string, any>;
    projectId: string;
    status?: string;
    createdAt: string;
    updatedAt: string;
  }
  
  const [dataSources, setDataSources] = useState<DataSourceType[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(false);

  const getDefaultTestInput = (nodeType: string | undefined, nodeData?: any) => {
    switch (nodeType) {
      case 'start':
        return 'workflow_started';
        
      case 'input':
        return 'Please enter your question about the system';
        
      case 'llm':
        // const model = nodeData?.model || 'gpt-4'; // Removed unused variable
        if (nodeData?.tool === 'responseEvaluation') {
          return 'The weather in New York today is sunny with a temperature of 75°F and light winds from the west.';
        } else if (nodeData?.label?.toLowerCase().includes('summarizer')) {
          return 'This is a long document about artificial intelligence and machine learning. It covers various topics including neural networks, deep learning, natural language processing, and computer vision. The document explains how these technologies are being used in different industries such as healthcare, finance, and autonomous vehicles. It also discusses the ethical implications and future prospects of AI development.';
        } else if (nodeData?.label?.toLowerCase().includes('analyzer')) {
          return 'What are the main issues with our manufacturing equipment?';
        } else {
          return 'What is the current status of work orders for asset PUMP-001?';
        }
        
      case 'tool':
        const tool = nodeData?.tool;
        switch (tool) {
          case 'webSearch':
            return 'latest developments in renewable energy 2024';
          case 'vectorSearch':
          case 'hybridSearch':
            return 'maintenance procedures for centrifugal pumps';
          case 'contextSelection':
            return 'Find the most relevant maintenance procedures for pump maintenance';
          case 'responseEvaluation':
            return 'The system is functioning normally with no critical alerts.';
          default:
            return 'search query for maintenance documentation';
        }
        
      case 'condition':
        return JSON.stringify({
          confidence: 0.85,
          threshold: 0.7,
          score: 8.5,
          value: 42,
          status: 'active'
        }, null, 2);
        
      case 'sql-query':
        const query = nodeData?.query || '';
        
        // If the configured query has variables, provide a similar example
        if (query.includes('{{')) {
          return `-- Example query with variables (will be replaced during execution)
SELECT id, name, email, status, created_at
FROM users 
WHERE status = '{{status}}' 
AND department = '{{department}}'
ORDER BY created_at DESC
LIMIT {{limit}}`;
        } else {
          // Provide generic example based on connector type
          const connectorType = nodeData?.dataSourceName?.toLowerCase() || '';
          if (connectorType.includes('mysql')) {
            return `-- Example MySQL query
SELECT id, name, email, status, created_at
FROM users 
WHERE status = 'active'
ORDER BY created_at DESC
LIMIT 10`;
          } else if (connectorType.includes('postgres')) {
            return `-- Example PostgreSQL query
SELECT id, name, email, status, created_at
FROM users 
WHERE status = 'active'
ORDER BY created_at DESC
LIMIT 10`;
          } else if (connectorType.includes('mssql') || connectorType.includes('sql server')) {
            return `-- Example MSSQL query
SELECT TOP 10 id, name, email, status, created_at
FROM users 
WHERE status = 'active'
ORDER BY created_at DESC`;
          } else {
            return `-- Example SQL query
SELECT TOP 10 *
FROM your_table_name 
WHERE status = 'active'
ORDER BY created_date DESC`;
          }
        }
        
      case 'http-request':
        const method = nodeData?.method || 'GET';
        const url = nodeData?.url || '';
        
        if (method === 'GET') {
          return JSON.stringify({
            userId: 'user_123',
            apiKey: 'demo_key',
            limit: 10
          }, null, 2);
        } else if (method === 'POST') {
          if (url.includes('notification') || url.includes('email')) {
            return JSON.stringify({
              recipient: 'maintenance@company.com',
              subject: 'System Alert',
              message: 'Equipment requires attention',
              priority: 'high'
            }, null, 2);
          } else {
            return JSON.stringify({
              name: 'New Work Order',
              description: 'Routine maintenance check',
              assignee: 'tech_001',
              dueDate: '2024-12-31'
            }, null, 2);
          }
        } else {
          return JSON.stringify({
            id: 'record_123',
            status: 'updated',
            modifiedBy: 'system'
          }, null, 2);
        }
        
      case 'data-transform':
        return JSON.stringify([
          { id: 1, name: 'Pump A', status: 'running', temperature: 85 },
          { id: 2, name: 'Pump B', status: 'maintenance', temperature: 72 },
          { id: 3, name: 'Pump C', status: 'running', temperature: 90 }
        ], null, 2);
        
      case 'email':
        return JSON.stringify({
          workOrderId: 'WO-2024-001',
          assetName: 'Centrifugal Pump A1',
          issue: 'Abnormal vibration detected',
          priority: 'High',
          technician: 'John Smith'
        }, null, 2);
        
      case 'file-operation':
        const operation = nodeData?.operation || 'read';
        if (operation === 'write') {
          return JSON.stringify({
            filename: 'maintenance_log.txt',
            content: 'Maintenance completed successfully at ' + new Date().toISOString(),
            append: true
          }, null, 2);
        } else {
          return JSON.stringify({
            filename: 'config.json',
            encoding: 'utf8'
          }, null, 2);
        }
        
      case 'cache':
        const cacheOp = nodeData?.operation || 'get';
        if (cacheOp === 'set') {
          return JSON.stringify({
            key: 'user_session_123',
            value: { userId: '123', lastAccess: new Date().toISOString() },
            ttl: 3600
          }, null, 2);
        } else {
          return JSON.stringify({
            key: 'user_session_123'
          }, null, 2);
        }
        
      case 'end':
        return 'Processing completed successfully';
        
      default:
        return 'test input for node processing';
    }
  };

  const loadDataSources = async () => {
    if (!projectId) return;
    
    setLoadingDataSources(true);
    try {
      // Load data sources for the current project
      const response = await apiUtils.backend.get(`/projects/${projectId}/data-sources`);
      
      const responseData = response.data;
      if (Array.isArray(responseData)) {
        setDataSources(responseData);
      } else {
        setDataSources([]);
      }
    } catch (error: any) {
      console.error('Failed to load data sources:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      messageApi.error(`Failed to load data sources: ${error.message || 'Unknown error'}`);
      setDataSources([]); // Ensure array on error
    } finally {
      setLoadingDataSources(false);
    }
  };

  useEffect(() => {
    if (visible && projectId) {
      loadDataSources();
    }
  }, [visible, projectId]);

  // Auto-refresh data sources when the modal opens for SQL Query nodes
  useEffect(() => {
    if (visible && node?.type === 'sql-query' && projectId && !loadingDataSources && dataSources.length === 0) {
      const timeoutId = setTimeout(() => {
        loadDataSources();
      }, 500); // Small delay to avoid rapid calls
      return () => clearTimeout(timeoutId);
    }
  }, [visible, node?.type, projectId, loadingDataSources, dataSources.length]);

  useEffect(() => {
    if (node && visible) {
      // Check if node has custom code
      const nodeHasCustomCode = !!(node.data.customCode && node.data.customCode.trim().length > 0);
      setHasCustomCode(nodeHasCustomCode);
      
      // Populate form with node data
      form.setFieldsValue({
        label: node.data.label,
        description: node.data.description,
        hasCustomCode: nodeHasCustomCode,
        ...node.data.config,
        tool: node.data.tool,
        model: node.data.model,
        condition: node.data.condition,
        // SQL Query fields
        dataSourceId: node.data.dataSourceId,
        query: node.data.query,
        outputFormat: node.data.outputFormat,
        resultLimit: node.data.resultLimit,
        timeout: node.data.timeout,
        // HTTP Request fields
        url: node.data.url,
        method: node.data.method,
        headers: typeof node.data.headers === 'object' ? JSON.stringify(node.data.headers, null, 2) : node.data.headers,
        body: typeof node.data.body === 'object' ? JSON.stringify(node.data.body, null, 2) : node.data.body,
        responseFormat: node.data.responseFormat,
      });
      
      // Set custom JS code if available
      setJsCode(node.data.customCode || getDefaultCode(node.type));
      
      // Set default test input based on node type and configuration
      setTestInput(getDefaultTestInput(node.type, node.data));
    }
  }, [node, visible, form]);

  const getDefaultCode = (nodeType: string | undefined) => {
    switch (nodeType) {
      case 'llm':
        return `// LLM Node Custom Logic
// Access the current state with: state
// Set output with: setOutput(value)

async function processLLM(state, setOutput, tools) {
  try {
    // Custom LLM processing logic here
    const prompt = \`System: You are a helpful assistant.
User: \${state.input}\`;
    
    // Use the configured model (available as config.model)
    const response = await tools.llm.invoke({
      model: config.model || 'gpt-4',
      messages: [{ role: 'user', content: prompt }],
      temperature: config.temperature || 0.7,
      maxTokens: config.maxTokens || 1000
    });
    
    setOutput(response.content);
    return { success: true };
  } catch (error) {
    console.error('LLM processing failed:', error);
    setOutput('Error in LLM processing');
    return { success: false, error: error.message };
  }
}`;
      
      case 'tool':
        return `// Tool Node - Hello World Example
// Access the current state with: state
// Set output with: setOutput(value)

async function processTool(state, setOutput, tools) {
  try {
    // Simple Hello World example
    const greeting = "Hello, World!";
    const currentTime = new Date().toLocaleString();
    const userInput = state.input || "No input provided";
    
    // Create a friendly response
    const response = \`\${greeting}
    
🎉 Welcome to your custom tool!
📅 Current time: \${currentTime}
📝 Your input: \${userInput}

This is a simple example. You can modify this code to:
• Process data
• Call APIs
• Transform information
• Connect to external services

Happy coding! 🚀\`;
    
    setOutput(response);
    return { success: true };
  } catch (error) {
    console.error('Tool processing failed:', error);
    setOutput('Error: Something went wrong');
    return { success: false, error: error.message };
  }
}`;
      
      case 'condition':
        return `// Condition Node Custom Logic
// Return true/false to control flow
// Access the current state with: state

function evaluateCondition(state) {
  try {
    // Custom condition logic here
    const condition = config.condition || 'true';
    
    // You can access state properties like:
    // state.input, state.confidence, state.data, etc.
    
    // Example conditions:
    if (condition.includes('confidence')) {
      return state.confidence >= (config.threshold || 0.7);
    }
    
    if (condition.includes('length')) {
      return state.input.length > (config.minLength || 10);
    }
    
    // Default: evaluate as JavaScript expression
    return eval(condition);
  } catch (error) {
    console.error('Condition evaluation failed:', error);
    return false;
  }
}`;
      
      default:
        return `// Custom Node Logic
// Access the current state with: state
// Set output with: setOutput(value)

async function processNode(state, setOutput, tools) {
  try {
    // Your custom logic here
    setOutput(\`Processed: \${state.input}\`);
    return { success: true };
  } catch (error) {
    console.error('Node processing failed:', error);
    setOutput('Error in processing');
    return { success: false, error: error.message };
  }
}`;
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (!node) return;

      const updatedData = {
        ...node.data,
        label: values.label,
        description: values.description,
        customCode: hasCustomCode ? jsCode : null,
        config: {
          ...node.data.config,
          ...values,
        },
      };

      // Type-specific updates
      if (node.type === 'llm') {
        updatedData.model = values.model;
        updatedData.config = {
          ...updatedData.config,
          temperature: values.temperature,
          maxTokens: values.maxTokens,
          systemPrompt: values.systemPrompt,
        };
      } else if (node.type === 'tool') {
        updatedData.tool = values.tool;
        updatedData.config = {
          ...updatedData.config,
          maxResults: values.maxResults,
          threshold: values.threshold,
        };
      } else if (node.type === 'condition') {
        updatedData.condition = values.condition;
        updatedData.config = {
          ...updatedData.config,
          threshold: values.threshold,
        };
      } else if (node.type === 'sql-query') {
        updatedData.dataSourceId = values.dataSourceId;
        updatedData.query = values.query;
        updatedData.outputFormat = values.outputFormat;
        updatedData.resultLimit = values.resultLimit;
        updatedData.timeout = values.timeout;
        
        // Find and set the data source name for display
        const selectedDataSource = dataSources.find(ds => ds.id === values.dataSourceId);
        if (selectedDataSource) {
          updatedData.dataSourceName = selectedDataSource.name;
        }
      } else if (node.type === 'http-request') {
        updatedData.url = values.url;
        updatedData.method = values.method;
        updatedData.responseFormat = values.responseFormat;
        updatedData.timeout = values.timeout;
        // Parse JSON strings for headers and body
        try {
          updatedData.headers = values.headers ? JSON.parse(values.headers) : {};
        } catch {
          updatedData.headers = {};
        }
        try {
          updatedData.body = values.body ? JSON.parse(values.body) : null;
        } catch {
          updatedData.body = values.body;
        }
      }

      console.log('Saving node with data:', JSON.stringify({
        nodeId: node.id,
        checkboxState: hasCustomCode,
        hasCustomCode: !!updatedData.customCode,
        customCodeLength: updatedData.customCode?.length || 0,
        customCodePreview: updatedData.customCode ? updatedData.customCode.substring(0, 100) + '...' : 'None'
      }, null, 2));
      
      onSave(node.id, updatedData);
      
      // Show reminder to save graph if custom code was added
      if (hasCustomCode && updatedData.customCode) {
        messageApi.warning('Node configuration saved. Please save the graph to persist changes!', 6);
      } else {
        messageApi.success('Node configuration saved!');
      }
      
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleTestNode = async () => {
    if (!node || !projectId) {
      messageApi.error('Cannot test node: missing node or project ID');
      return;
    }

    setIsTesting(true);
    setTestOutput('');

    // Special handling for SQL Query nodes
    if (node.type === 'sql-query') {
      try {
        const values = await form.validateFields();
        
        if (!values.dataSourceId || !values.query) {
          messageApi.error('Please select a data source and enter a SQL query');
          setTestOutput('❌ Missing required fields: Data source and SQL query are required for testing.');
          setIsTesting(false);
          return;
        }

        // Test SQL query directly using the test input
        const queryToTest = testInput.trim() || values.query;
        console.log('=== Frontend SQL Test Execution ===');
        console.log('Project ID:', projectId);
        console.log('Data Source ID:', values.dataSourceId);
        console.log('Query to test:', queryToTest);
        console.log('Output Format:', values.outputFormat || 'json');
        console.log('Result Limit:', values.resultLimit || 100);
        
        const response = await apiUtils.backend.post(`/projects/${projectId}/data-sources/${values.dataSourceId}/test-query`, {
          query: queryToTest,
          outputFormat: values.outputFormat || 'json',
          resultLimit: values.resultLimit || 100
        });
        
        console.log('API Response:', response.data);

        if (response.data.success) {
          const result = response.data;
          const metadata = result.metadata || {};
          
          let outputText = '';
          
          // Format based on output format
          switch (values.outputFormat) {
            case 'table':
              if (result.data.columns && result.data.rows) {
                outputText = `📊 Query Results (Table Format):\n\n`;
                outputText += `Columns: ${result.data.columns.join(', ')}\n`;
                outputText += `Records: ${result.data.rows.length}\n\n`;
                outputText += `Sample Data:\n`;
                result.data.rows.slice(0, 5).forEach((row: any, index: number) => {
                  outputText += `Row ${index + 1}: ${JSON.stringify(row)}\n`;
                });
              } else {
                outputText = JSON.stringify(result.data, null, 2);
              }
              break;
              
            case 'csv':
              outputText = `📄 Query Results (CSV Format):\n\n${result.data}`;
              break;
              
            case 'count':
              outputText = `🔢 Query Results (Count Format):\n\n`;
              outputText += `Total Records: ${result.data.count}\n\n`;
              outputText += `Sample Records:\n${JSON.stringify(result.data.sample, null, 2)}`;
              break;
              
            case 'json':
            default:
              outputText = `📋 Query Results (JSON Format):\n\n${JSON.stringify(result.data, null, 2)}`;
              break;
          }
          
          outputText += `\n\n📈 Execution Metadata:`;
          outputText += `\n• Records Returned: ${metadata.recordCount || 0}`;
          outputText += `\n• Execution Time: ${metadata.executionTime || 'Unknown'}`;
          outputText += `\n• Data Source: ${metadata.dataSourceName || values.dataSourceId}`;
          outputText += `\n• Output Format: ${metadata.outputFormat || 'json'}`;
          
          setTestOutput(outputText);
          messageApi.success('SQL Query executed successfully!');
        } else {
          setTestOutput(`❌ SQL Query Failed:\n\n${response.data.error || 'Unknown error'}\n\nQuery: ${values.query}`);
          messageApi.error('SQL Query execution failed');
        }
        
        setIsTesting(false);
        return;
      } catch (error: any) {
        console.error('SQL test failed:', error);
        setTestOutput(`❌ SQL Test Error:\n\n${error.message || 'Unknown error occurred'}\n\nPlease check your query and data source configuration.`);
        messageApi.error('Failed to test SQL query');
        setIsTesting(false);
        return;
      }
    }

    try {
      // Get current form values - handle validation errors gracefully
      let values;
      try {
        values = await form.validateFields();
      } catch (validationError: any) {
        // Handle validation errors gracefully
        const errorFields = validationError?.errorFields || [];
        const missingFields = errorFields.map((field: any) => field.name?.[0] || 'unknown').join(', ');
        
        messageApi.error(`Please fill in required fields: ${missingFields}`);
        setTestOutput(`❌ Node test failed: Missing required fields: ${missingFields}\n\nPlease complete the node configuration before testing.`);
        setIsTesting(false);
        return;
      }
      
      // Create a test node configuration
      const testNodeData = {
        id: `test-${node.id}`,
        type: node.type,
        data: {
          ...node.data,
          label: values.label,
          description: values.description,
          customCode: jsCode,
          tool: values.tool || node.data.tool,
          model: values.model || node.data.model,
          condition: values.condition || node.data.condition,
          // System node fields
          dataSourceId: values.dataSourceId || node.data.dataSourceId,
          query: values.query || node.data.query,
          outputFormat: values.outputFormat || node.data.outputFormat,
          resultLimit: values.resultLimit || node.data.resultLimit,
          timeout: values.timeout || node.data.timeout,
          url: values.url || node.data.url,
          method: values.method || node.data.method,
          responseFormat: values.responseFormat || node.data.responseFormat,
          headers: (() => {
            try {
              return values.headers ? JSON.parse(values.headers) : node.data.headers;
            } catch {
              return node.data.headers || {};
            }
          })(),
          body: (() => {
            try {
              return values.body ? JSON.parse(values.body) : node.data.body;
            } catch {
              return values.body || node.data.body;
            }
          })(),
          config: {
            ...node.data.config,
            ...values,
          },
        },
        position: { x: 0, y: 0 }
      };

      // Create a minimal test graph with just this node
      const testGraph = {
        name: `Test Graph for ${node.id}`,
        description: 'Temporary graph for node testing',
        nodes: [
          {
            id: 'start',
            type: 'start',
            data: { label: 'Start' },
            position: { x: 0, y: 0 }
          },
          testNodeData,
          {
            id: 'end',
            type: 'end',
            data: { label: 'End' },
            position: { x: 200, y: 0 }
          }
        ],
        edges: [
          {
            id: 'start-to-test',
            source: 'start',
            target: testNodeData.id
          },
          {
            id: 'test-to-end',
            source: testNodeData.id,
            target: 'end'
          }
        ],
        metadata: {
          isTestGraph: true,
          originalNodeId: node.id
        }
      };

      // Create temporary graph
      console.log('Creating test graph:', testGraph); // Debug log
      const createResponse = await apiUtils.backend.post(`/projects/${projectId}/graphs`, testGraph);
      const tempGraphId = createResponse.data.id;
      console.log('Created test graph with ID:', tempGraphId); // Debug log

      try {
        // Execute the test graph
        console.log('Executing test graph...'); // Debug log
        const executeResponse = await apiUtils.backend.post(`/projects/${projectId}/graphs/${tempGraphId}/execute`, {
          input: testInput,
          initialState: {
            confidence: 0.0,
            metadata: {
              startTime: new Date().toISOString(),
              isTest: true
            }
          }
        });

        const result = executeResponse.data;
        
        console.log('Test execution result:', result); // Debug log
        
        if (result.success) {
          const output = result.finalState?.output || result.output || 'No output generated';
          setTestOutput(output);
          messageApi.success('Node test completed successfully!');
        } else {
          const errorMsg = result.error || 'Unknown error';
          setTestOutput(`Test failed: ${errorMsg}\n\nFull result: ${JSON.stringify(result, null, 2)}`);
          messageApi.error('Node test failed');
        }

      } finally {
        // Clean up temporary graph
        try {
          await apiUtils.backend.delete(`/projects/${projectId}/graphs/${tempGraphId}`);
        } catch (cleanupError) {
          console.warn('Failed to cleanup test graph:', cleanupError);
        }
      }

    } catch (error) {
      console.error('Node test failed:', error);
      
      // Show more detailed error information
      let errorMsg = 'Unknown error';
      if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      } else if (error && typeof error === 'object') {
        errorMsg = JSON.stringify(error, null, 2);
      }
      
      setTestOutput(`Test error: ${errorMsg}`);
      messageApi.error('Failed to test node');
    } finally {
      setIsTesting(false);
    }
  };

  const getNodeIcon = (type: string | undefined) => {
    switch (type) {
      case 'llm': return <BrainCircuit className="h-4 w-4" />;
      case 'tool': return <Wrench className="h-4 w-4" />;
      case 'condition': return <GitBranch className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  const renderBasicConfig = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Form.Item
        name="label"
        label="Node Label"
        rules={[{ required: true, message: 'Please enter a node label' }]}
      >
        <Input placeholder="Enter node label" />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <TextArea rows={2} placeholder="Enter node description" />
      </Form.Item>

      <Form.Item name="hasCustomCode" valuePropName="checked">
        <Checkbox 
          checked={hasCustomCode}
          onChange={(e) => {
            setHasCustomCode(e.target.checked);
            if (!e.target.checked) {
              // Reset to default code when disabling custom code
              setJsCode(getDefaultCode(node?.type));
              // Switch back to basic tab if we were on code tab
              if (activeTab === 'code') {
                setActiveTab('basic');
              }
            } else {
              // Switch to code tab when enabling custom code
              setActiveTab('code');
            }
          }}
        >
          Use Custom JavaScript Code
        </Checkbox>
      </Form.Item>

      {node?.type === 'llm' && (
        <>
          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please select a model' }]}
          >
            <Select placeholder="Select LLM model">
              <Option value="gpt-4">GPT-4</Option>
              <Option value="gpt-4-turbo-preview">GPT-4 Turbo</Option>
              <Option value="gpt-3.5-turbo">GPT-3.5 Turbo</Option>
              <Option value="claude-3-opus">Claude 3 Opus</Option>
              <Option value="claude-3-sonnet">Claude 3 Sonnet</Option>
            </Select>
          </Form.Item>

          <Form.Item name="temperature" label="Temperature">
            <InputNumber
              min={0}
              max={2}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="0.7"
            />
          </Form.Item>

          <Form.Item name="maxTokens" label="Max Tokens">
            <InputNumber
              min={1}
              max={4000}
              style={{ width: '100%' }}
              placeholder="1000"
            />
          </Form.Item>

          <Form.Item name="systemPrompt" label="System Prompt">
            <TextArea rows={3} placeholder="You are a helpful assistant..." />
          </Form.Item>
        </>
      )}

      {node?.type === 'tool' && (
        <>
          <Form.Item
            name="tool"
            label="Tool Type"
            rules={[{ required: true, message: 'Please select a tool' }]}
          >
            <Select placeholder="Select tool">
              <Option value="webSearch">Web Search</Option>
              <Option value="vectorSearch">Vector Search</Option>
              <Option value="hybridSearch">Hybrid Search</Option>
              <Option value="contextSelection">Context Selection</Option>
              <Option value="responseEvaluation">Response Evaluation</Option>
              <Option value="custom">Custom Tool</Option>
            </Select>
          </Form.Item>

          <Form.Item name="maxResults" label="Max Results">
            <InputNumber
              min={1}
              max={100}
              style={{ width: '100%' }}
              placeholder="10"
            />
          </Form.Item>

          <Form.Item name="threshold" label="Threshold">
            <InputNumber
              min={0}
              max={1}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="0.7"
            />
          </Form.Item>
        </>
      )}

      {node?.type === 'condition' && (
        <>
          <Form.Item
            name="condition"
            label="Condition Expression"
            rules={[{ required: true, message: 'Please enter a condition' }]}
          >
            <Input placeholder="confidence >= 0.7" />
          </Form.Item>

          <Form.Item name="threshold" label="Threshold Value">
            <InputNumber
              min={0}
              max={1}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="0.7"
            />
          </Form.Item>
        </>
      )}

      {node?.type === 'sql-query' && (
        <>
          <Form.Item
            name="dataSourceId"
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Data Source</span>
                <Button 
                  type="text" 
                  size="small" 
                  onClick={loadDataSources}
                  loading={loadingDataSources}
                  style={{ minWidth: 'auto', padding: '0 4px' }}
                >
                  🔄
                </Button>
              </div>
            }
            rules={[{ required: true, message: 'Please select a data source' }]}
          >
            <Select 
              placeholder="Select data source" 
              loading={loadingDataSources}
              popupMatchSelectWidth={false}
              dropdownRender={(menu) => (
                <div style={{ zIndex: 2050 }}>{menu}</div>
              )}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
            >
          
              {/* Real SQL Data Sources from API */}
              {dataSources
                .filter(ds => ds && ds.id && ['mssql', 'mysql', 'postgresql', 'postgres', 'sqlite', 'oracle'].includes(ds.connectorType?.toLowerCase()))
                .map((ds) => (
                  <Option key={ds.id} value={ds.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{ds.name || 'Unnamed Source'}</span>
                      <span style={{ fontSize: '12px', color: '#666' }}>({ds.connectorType || 'Unknown'})</span>
                    </div>
                  </Option>
                ))}
              
              {/* Create new option */}
              {dataSources.filter(ds => ds && ds.id && ['mssql', 'mysql', 'postgresql', 'postgres', 'sqlite', 'oracle'].includes(ds.connectorType?.toLowerCase())).length > 0 && (
                <Option disabled style={{ borderTop: '1px solid #f0f0f0', padding: '8px' }}>
                  <Button 
                    type="text" 
                    size="small" 
                    block
                    onClick={() => {
                      window.open('/admin/datasources', '_blank');
                    }}
                    style={{ textAlign: 'left' }}
                  >
                    + Create New Data Source
                  </Button>
                </Option>
              )}
              
            </Select>
          </Form.Item>

          {dataSources.filter(ds => ds && ds.id && ['mssql', 'mysql', 'postgresql', 'postgres', 'sqlite', 'oracle'].includes(ds.connectorType?.toLowerCase())).length === 0 && !loadingDataSources && (
            <Alert
              message="No SQL Data Sources Found"
              description={
                <div>
                  <p>SQL Query nodes require SQL database connections (MySQL, PostgreSQL, MSSQL, Oracle, SQLite).</p>
                  <p>Please create a SQL data source to use SQL Query nodes:</p>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={() => window.open('/admin/datasources', '_blank')}
                    style={{ marginTop: '8px' }}
                  >
                    Create SQL Data Source
                  </Button>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          <Form.Item
            name="query"
            label="SQL Query"
            rules={[{ required: true, message: 'Please enter SQL query' }]}
          >
            <TextArea
              rows={6}
              placeholder="SELECT * FROM table_name WHERE condition = {{variable}}"
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </Form.Item>

          <Form.Item name="outputFormat" label="Output Format">
            <Select placeholder="Select output format" defaultValue="json">
              <Option value="json">JSON</Option>
              <Option value="table">Table</Option>
              <Option value="csv">CSV</Option>
              <Option value="count">Count Only</Option>
            </Select>
          </Form.Item>

          <Form.Item name="resultLimit" label="Result Limit">
            <InputNumber
              min={1}
              max={10000}
              style={{ width: '100%' }}
              placeholder="100"
            />
          </Form.Item>

          <Form.Item name="timeout" label="Timeout (ms)">
            <InputNumber
              min={1000}
              max={300000}
              style={{ width: '100%' }}
              placeholder="30000"
            />
          </Form.Item>
        </>
      )}

      {node?.type === 'http-request' && (
        <>
          <Form.Item
            name="url"
            label="URL"
            rules={[
              { required: true, message: 'Please enter URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="https://api.example.com/data" />
          </Form.Item>

          <Form.Item
            name="method"
            label="HTTP Method"
            rules={[{ required: true, message: 'Please select HTTP method' }]}
          >
            <Select placeholder="Select HTTP method" defaultValue="GET">
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
              <Option value="PATCH">PATCH</Option>
            </Select>
          </Form.Item>

          <Form.Item name="headers" label="Headers (JSON)">
            <TextArea
              rows={3}
              placeholder='{"Authorization": "Bearer {{token}}", "Content-Type": "application/json"}'
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </Form.Item>

          <Form.Item name="body" label="Request Body (JSON)">
            <TextArea
              rows={4}
              placeholder='{"key": "{{value}}", "data": "example"}'
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </Form.Item>

          <Form.Item name="responseFormat" label="Response Format">
            <Select placeholder="Select response format" defaultValue="json">
              <Option value="json">JSON</Option>
              <Option value="text">Text</Option>
              <Option value="blob">Binary/Blob</Option>
            </Select>
          </Form.Item>

          <Form.Item name="timeout" label="Timeout (ms)">
            <InputNumber
              min={1000}
              max={300000}
              style={{ width: '100%' }}
              placeholder="30000"
            />
          </Form.Item>
        </>
      )}
    </Space>
  );

  const renderCodeEditor = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        message="Custom JavaScript Code"
        description="Write custom logic for this node. The code will be executed in a secure sandbox environment."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Card>
        <div 
          style={{ 
            position: 'relative', 
            zIndex: 2001,
            height: '400px'
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Editor
            height="400px"
            defaultLanguage="javascript"
            value={jsCode}
            onChange={(value) => setJsCode(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              wordWrap: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              selectOnLineNumbers: true,
              roundedSelection: false,
              readOnly: false,
              cursorStyle: 'line',
            }}
          />
        </div>
      </Card>

      <Alert
        message="Available Objects"
        description={
          <div>
            <strong>state</strong>: Current workflow state<br />
            <strong>setOutput(value)</strong>: Set node output<br />
            <strong>tools</strong>: Available tools (webSearch, vectorSearch, etc.)<br />
            <strong>config</strong>: Node configuration object
          </div>
        }
        type="success"
        showIcon
      />
    </Space>
  );

  const renderTestTab = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        message="Test Node Execution"
        description="Test your node configuration with sample input to see the output before saving."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Card 
        title="Test Input" 
        extra={
          <Button 
            size="small" 
            onClick={() => setTestInput(getDefaultTestInput(node?.type, node?.data))}
            type="link"
          >
            Load Sample Input
          </Button>
        }
      >
        <TextArea
          rows={node?.type === 'sql-query' ? 6 : 4}
          value={testInput}
          onChange={(e) => setTestInput(e.target.value)}
          placeholder={
            node?.type === 'sql-query' 
              ? "Enter SQL query to test (e.g., SELECT * FROM table_name WHERE condition = 'value')"
              : node?.type === 'http-request'
              ? "Enter test data or parameters for the HTTP request"
              : node?.type === 'llm'
              ? "Enter text prompt or question for the LLM to process"
              : node?.type === 'tool'
              ? "Enter search query or input for the tool"
              : "Enter test input for your node..."
          }
          style={{ 
            fontFamily: node?.type === 'sql-query' ? 'monospace' : 'inherit',
            fontSize: node?.type === 'sql-query' ? '13px' : '14px'
          }}
        />
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
          💡 Sample input is automatically generated based on your node type and configuration
        </div>
      </Card>

      <div style={{ textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={handleTestNode}
          loading={isTesting}
          disabled={!projectId || !testInput.trim()}
          size="large"
        >
          {isTesting ? 'Testing Node...' : 'Test Node'}
        </Button>
      </div>

      <Card title="Test Output" style={{ minHeight: '200px' }}>
        {isTesting ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666' }}>
              Executing node test...
            </div>
          </div>
        ) : testOutput ? (
          <div style={{ 
            whiteSpace: 'pre-wrap', 
            backgroundColor: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}>
            {testOutput}
          </div>
        ) : (
          <div style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>
            No test output yet. Click "Test Node" to run a test.
          </div>
        )}
      </Card>

      {!projectId && (
        <Alert
          message="Project ID Required"
          description="A project ID is required to test nodes. Please save your graph first."
          type="warning"
          showIcon
        />
      )}
    </Space>
  );

  if (!node) return null;

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          {getNodeIcon(node.type)}
          <span>Configure {node.type?.toUpperCase()} Node</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      width={800}
      okText="Save Configuration"
      cancelText="Cancel"
      style={{ zIndex: 2000 }}
      mask={true}
      maskClosable={false}
      destroyOnHidden={true}
    >
      <Form form={form} layout="vertical">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Configuration',
              children: renderBasicConfig(),
            },
            ...(hasCustomCode ? [{
              key: 'code',
              label: 'Custom Code',
              children: renderCodeEditor(),
            }] : []),
            {
              key: 'test',
              label: 'Test Node',
              children: renderTestTab(),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};

export default NodeConfigurationModal;